import React, { useState, useEffect, ChangeEvent } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Select } from 'components/forms/Selects'
import { RadioGroup } from 'transfix-ui/components/RadioGroup'
import { ICompanyInfo, ILocalCompanyInfo } from 'types/carrier'
import {
  carbCompliant,
  disadvantagedBusinessEntity,
  entityTypeOptions,
  convertToBoolean,
  convertToString,
} from 'apps/carrier/constants/companyInfo'
import {
  closeCarrierInformationDialog,
  editCompanyInfo,
} from 'apps/carrier/utils/actions/carrierInfo'
import { IStore } from 'apps/carrier/utils/reducers'
import { TextInput } from 'components/atoms'
import { FormModal } from 'transfix-ui'

interface ISelector {
  id?: string
  info: ICompanyInfo
  showDialog: boolean
  dialogError?: string
  updating?: boolean
}

interface IDropdownOption {
  label: string
  value: string
}

export const CompanyInformationDialog = () => {
  const dispatch = useDispatch()
  const { dialogError, id, info, showDialog, updating } = useSelector<IStore, ISelector>(
    ({ carrier, companyInfo }) => ({
      id: carrier.carrierDetails?.id,
      info: companyInfo.info,
      showDialog: companyInfo.showDialog,
      dialogError: companyInfo.dialogError,
      updating: companyInfo.updating,
    })
  )
  const closeDialogHandler = () => {
    dispatch(closeCarrierInformationDialog())
  }
  const [localCompanyInfo, setLocalCompanyInfo] = useState<ILocalCompanyInfo>({
    legalName: '',
    mcNumber: '',
    dbaName: '',
    dotNumber: '',
    ein: '',
    isCarbCompliant: 'unknown',
    eldCompliant: 'unknown',
    isDisadvantagedBusinessEntity: 'unknown',
  })
  useEffect(() => {
    if (showDialog) {
      setLocalCompanyInfo({
        ...info,
        isCarbCompliant: convertToString(info.isCarbCompliant),
        eldCompliant: convertToString(info.eldCompliant),
        isDisadvantagedBusinessEntity: convertToString(info.isDisadvantagedBusinessEntity),
      })
    }
  }, [showDialog])
  const {
    dbaName,
    dotNumber,
    dotNumberError,
    ein,
    einError,
    entityType,
    isCarbCompliant,
    isDisadvantagedBusinessEntity,
    legalName,
    legalNameError,
    mcNumber,
  } = localCompanyInfo

  const updateLocalCompanyInfo = ({
    target: { id: targetId, value },
  }: ChangeEvent<HTMLInputElement>) => {
    setLocalCompanyInfo({ ...localCompanyInfo, [targetId]: value || null })
  }

  const updateEntityType = (option: IDropdownOption) => {
    const val = option.value as ICompanyInfo['entityType']
    setLocalCompanyInfo({ ...localCompanyInfo, entityType: val })
  }

  const updateRadio = (e: React.FormEvent<HTMLInputElement>) => {
    setLocalCompanyInfo({ ...localCompanyInfo, [e.currentTarget.name]: e.currentTarget.value })
  }

  const validCompanyInfo = () => {
    const errors = { legalNameError: '', dotNumberError: '', einError: '' }
    if (!localCompanyInfo.legalName) {
      errors.legalNameError = 'Legal Name is required'
    }
    if (!localCompanyInfo.dotNumber) {
      errors.dotNumberError = 'DOT Number is required'
    }
    if (localCompanyInfo.ein && localCompanyInfo.ein.length !== 9) {
      errors.einError = 'EIN must be 9 characters'
    }
    if (errors.legalNameError || errors.dotNumberError || errors.einError) {
      setLocalCompanyInfo({ ...localCompanyInfo, ...errors })
      return false
    }
    return true
  }

  const saveCompanyInfo = () => {
    if (validCompanyInfo()) {
      const unconvertedlocalCompanyInfo = {
        ...localCompanyInfo,
        isCarbCompliant: convertToBoolean(localCompanyInfo.isCarbCompliant),
        eldCompliant: convertToBoolean(localCompanyInfo.eldCompliant),
        isDisadvantagedBusinessEntity: convertToBoolean(
          localCompanyInfo.isDisadvantagedBusinessEntity
        ),
      }
      dispatch(editCompanyInfo(id!, unconvertedlocalCompanyInfo))
    }
  }

  return (
    <FormModal
      title='Edit Company Information'
      size={6}
      open={showDialog}
      onClose={closeDialogHandler}
      error={dialogError}
      dataTest='modal-edit-company-information'
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          disabled: updating,
        },
        {
          label: 'Save',
          type: 'confirm',
          disabled: updating,
          action: saveCompanyInfo,
        },
      ]}>
      <div className='row'>
        <div className='col-xs-6'>
          <TextInput
            type='text'
            placeholder='Legal Name'
            label='Legal Name'
            id='legalName'
            value={legalName}
            onChange={updateLocalCompanyInfo}
            invalid={Boolean(legalNameError)}
            validationMessage={legalNameError}
            dataTest='legal-name'
          />
        </div>
        <div className='col-xs-4'>
          <TextInput
            type='number'
            placeholder='MC Number'
            label='MC Number'
            id='mcNumber'
            value={mcNumber}
            onChange={updateLocalCompanyInfo}
            dataTest='mc-number'
            // disallow editing mcNumber if it has been set before
            clearable={!info.mcNumber}
            disabled={Boolean(info.mcNumber)}
            readOnly={Boolean(info.mcNumber)}
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-xs-6'>
          <TextInput
            type='text'
            placeholder='DBA'
            label='DBA'
            id='dbaName'
            value={dbaName}
            onChange={updateLocalCompanyInfo}
            dataTest='dba-name'
          />
        </div>
        <div className='col-xs-4'>
          <TextInput
            type='number'
            placeholder='DOT Number'
            label='DOT Number'
            id='dotNumber'
            value={dotNumber}
            onChange={updateLocalCompanyInfo}
            invalid={Boolean(dotNumberError)}
            validationMessage={dotNumberError}
            dataTest='dot-number'
            // disallow editing dotNumber if it has been set before
            clearable={!info.dotNumber}
            disabled={Boolean(info.dotNumber)}
            readOnly={Boolean(info.dotNumber)}
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-xs-4'>
          <TextInput
            type='number'
            placeholder='EIN'
            label='EIN'
            id='ein'
            value={ein}
            onChange={updateLocalCompanyInfo}
            invalid={Boolean(einError)}
            validationMessage={einError}
            dataTest='ein'
          />
        </div>
      </div>
      <div className='pv2'>
        <div className='row'>
          <div className='col-xs-4'>
            <Select
              id='entityType'
              label='Entity Type'
              options={entityTypeOptions}
              value={entityTypeOptions.find(opt => opt.value === entityType)}
              onChange={updateEntityType}
            />
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-xs-4'>
          <RadioGroup
            label='CARB Compliant'
            name='isCarbCompliant'
            required={false}
            onOptionChange={updateRadio}
            checked={isCarbCompliant}
            options={carbCompliant}
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-xs-4'>
          <RadioGroup
            label='D/M/WBE'
            name='isDisadvantagedBusinessEntity'
            required={false}
            onOptionChange={updateRadio}
            checked={isDisadvantagedBusinessEntity}
            options={disadvantagedBusinessEntity}
          />
        </div>
      </div>
    </FormModal>
  )
}
